import { useTranslations } from "use-intl";
import { useNavigate } from "@tanstack/react-router";
import { AirplaneTiltIcon, BuildingsIcon } from "@phosphor-icons/react";
import { rudderAnalytics, SearchTrackingEvents } from "../lib/rudderstack";
import { Feature, isFeatureDisabled } from "../lib/utils/featureFlags";
import type { SearchScope } from "../lib/types/types";
import { IconTrips } from "../icons";
import { ScopeSwitcherBase, ScopeSwitcherButton } from "./ScopeSwitcherBase";

interface ScopeSwitcherProps {
  current: SearchScope;
}

const eventMap = {
  flights: SearchTrackingEvents.USER_SWITCHED_TO_FLIGHT_SEARCH,
  hotels: SearchTrackingEvents.USER_SWITCHED_TO_HOTEL_SEARCH,
  trips: SearchTrackingEvents.USER_SWITCHED_TO_TRIPS,
} as const;

export const ScopeSwitcher = ({ current }: ScopeSwitcherProps) => {
  const t = useTranslations();
  const navigate = useNavigate();

  return (
    <ScopeSwitcherBase
      color="primary"
      value={current}
      onChange={(newValue) => {
        if (newValue && eventMap[newValue]) {
          rudderAnalytics.track(eventMap[newValue]);
        }
        navigate({
          to: "/",
          search: {
            category: newValue || current,
          },
          replace: true,
        });
      }}
      hidden={
        isFeatureDisabled(Feature.HotelsFlow) &&
        isFeatureDisabled(Feature.TripsFlow)
      }
    >
      <ScopeSwitcherButton value="flights">
        <AirplaneTiltIcon size={48} aria-hidden className="shrink-0 p-3" />
        {t("flights.title")}
      </ScopeSwitcherButton>

      {isFeatureDisabled(Feature.HotelsFlow) ? null : (
        <ScopeSwitcherButton value="hotels">
          <BuildingsIcon size={48} aria-hidden className="shrink-0 p-3" />
          {t("hotels.title")}
        </ScopeSwitcherButton>
      )}

      {isFeatureDisabled(Feature.TripsFlow) ? null : (
        <ScopeSwitcherButton value="trips" badgeContent={t("trips.newBadge")}>
          <IconTrips aria-hidden className="size-12 shrink-0 p-3" />
          {t("trips.title")}
        </ScopeSwitcherButton>
      )}
    </ScopeSwitcherBase>
  );
};
